import { useCallback, useEffect, useRef, useState } from "react";
import {
  CONTENTS_NAV_SCROLL,
  computeActiveHeadingIndex,
  getScrollableParent,
  resolveHeadingElementById,
} from "./contentsNavScroll";

interface UseContentsNavSyncResult {
  activeId: string;
  navigateToSection: (id: string) => void;
}

/**
 * Subscribes to scroll/resize, resolves active TOC id from heading positions,
 * and exposes click navigation with suppress + resync for smooth scrolling.
 */
export function useContentsNavSync(headingIds: string[]): UseContentsNavSyncResult {
  const [activeId, setActiveId] = useState("");
  const suppressSyncUntilRef = useRef(0);
  const syncActiveHeadingRef = useRef<() => void>(() => {});
  const postClickSyncTimeoutRef = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (postClickSyncTimeoutRef.current != null) {
        clearTimeout(postClickSyncTimeoutRef.current);
        postClickSyncTimeoutRef.current = null;
      }
    },
    [],
  );

  useEffect(() => {
    if (!headingIds.length) {
      setActiveId("");
      return;
    }

    const headings = headingIds
      .map((id) => document.getElementById(id))
      .filter((el): el is HTMLElement => Boolean(el));

    if (!headings.length) {
      setActiveId("");
      return;
    }

    const scrollContainer = getScrollableParent(headings[0]);
    const isWindowScroll = !scrollContainer;
    let rafId = 0;
    let ticking = false;

    const syncActiveHeading = () => {
      if (Date.now() < suppressSyncUntilRef.current) return;

      const scrollTop = isWindowScroll ? window.scrollY : scrollContainer.scrollTop;
      const viewportBottom = isWindowScroll
        ? window.scrollY + window.innerHeight
        : scrollContainer.scrollTop + scrollContainer.clientHeight;
      const contentHeight = isWindowScroll
        ? document.documentElement.scrollHeight
        : scrollContainer.scrollHeight;

      const index = computeActiveHeadingIndex(headings, {
        scrollTop,
        isWindowScroll,
        scrollContainer,
        viewportBottom,
        contentHeight,
      });
      setActiveId(headings[index].id);
    };

    syncActiveHeadingRef.current = syncActiveHeading;

    const onScrollOrResize = () => {
      if (ticking) return;
      ticking = true;
      rafId = window.requestAnimationFrame(() => {
        syncActiveHeading();
        ticking = false;
      });
    };

    syncActiveHeading();
    const eventTarget: Window | HTMLElement = scrollContainer || window;
    eventTarget.addEventListener("scroll", onScrollOrResize, { passive: true });
    window.addEventListener("resize", onScrollOrResize);

    return () => {
      syncActiveHeadingRef.current = () => {};
      eventTarget.removeEventListener("scroll", onScrollOrResize as EventListener);
      window.removeEventListener("resize", onScrollOrResize);
      if (rafId) window.cancelAnimationFrame(rafId);
    };
  }, [headingIds]);

  const navigateToSection = useCallback((id: string) => {
    const section = resolveHeadingElementById(id);
    if (!section) return;

    const { CLICK_SYNC_SUPPRESS_MS } = CONTENTS_NAV_SCROLL;

    suppressSyncUntilRef.current = Date.now() + CLICK_SYNC_SUPPRESS_MS;
    setActiveId(id);
    section.scrollIntoView({ behavior: "smooth", block: "start", inline: "nearest" });
    window.history.replaceState(null, "", `#${id}`);

    if (postClickSyncTimeoutRef.current != null) {
      clearTimeout(postClickSyncTimeoutRef.current);
    }
    postClickSyncTimeoutRef.current = window.setTimeout(() => {
      postClickSyncTimeoutRef.current = null;
      suppressSyncUntilRef.current = 0;
      syncActiveHeadingRef.current();
    }, CLICK_SYNC_SUPPRESS_MS) as unknown as number;
  }, []);

  return { activeId, navigateToSection };
}
